import React, { useCallback, useEffect, useState } from 'react'
import './DeleteProfile.scss'
import { ProfileIcon } from '@components/Profile/ProfileIcon/ProfileIcon'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { VelvetSDK } from 'sdk'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import SpatialNavigation from '@utils/spatial-navigation.js'
import { onEnterKeyPressAndClick } from '@utils/helpers'
import { useNavigate, useParams, Params } from 'react-router-dom'
import { setError } from '@actions/errorCodeActions'
import { useDispatch } from 'react-redux'
import { ErrorActions, LocalStorageItem } from '@constants/index'
import { useTypedSelector } from 'store'
import { routes } from '@constants/routes.const'
import { useUserProfile } from '@hooks/useUserProfile'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import classNames from 'classnames'
import { setParentalControl, setProfileContentClassification, setProfilePIN } from '@actions/profileActions'
import { useUserProfiles } from '@hooks/useUserProfiles'

interface DeleteProfileProps {
  showMenu: (status: boolean) => void
}

export const DeleteProfile = ({ showMenu }: DeleteProfileProps) => {
  const V = VelvetSDK.Vocabulary.vocabulary
  const SN = SpatialNavigation
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const profiles = useTypedSelector((state) => state.profile.profiles)
  const { profileId } = useParams<Params>()
  const { profileData, isLoading } = useUserProfile({
    profileId: profileId || '',
  })
  const onlyOneProfile = profiles.length === 1
  const activeProfileObject = JSON.parse(localStorage.getItem(LocalStorageItem.ACTIVE_PROFILE) as string)
  const [loadingAction, setLoadingAction] = useState(false)

  const keepOrCreateNewProfileAction = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      onEnterKeyPressAndClick(e, () => {
        if (onlyOneProfile) {
          navigate(routes.MANAGE_PROFILES)
        } else {
          navigate(-1)
        }
      })
    },
    [navigate, onlyOneProfile],
  )

  const deleteProfileOrCancelAction = useCallback(
    async (e: React.KeyboardEvent | React.MouseEvent) => {
      const errorUpdate = {
        showErrorModal: true,
        fetchAction: ErrorActions.DELETE_PROFILE,
      }
      onEnterKeyPressAndClick(e, async () => {
        try {
          if (onlyOneProfile) {
            navigate(-1)
            return
          }

          const profileIdToDelete = profileData?.identifier || ''
          const activeProfileId = activeProfileObject?.identifier
          setLoadingAction(true)

          const response = await VelvetSDK.Storage.Profiles.service.deleteProfile({
            profileId: profileData?.identifier || '',
          })

          // Deletion succeeded if the backend returned a success message
          if (response?.message) {
            // If the deleted profile was currently active => clear it
            if (profileIdToDelete === activeProfileId) {
              localStorage.removeItem(LocalStorageItem.ACTIVE_PROFILE)
            }

            // clear the profile store actions
            dispatch(setProfileContentClassification(''))
            dispatch(setProfilePIN(''))
            dispatch(setParentalControl(null))
            navigate(routes.MANAGE_PROFILES)
            return
          }

          // Backend returned an unexpected response => show error
          dispatch(setError(errorUpdate))
        } catch (err) {
          console.error(err)
          setLoadingAction(false)
          dispatch(setError(errorUpdate))
        }
      })
    },
    [activeProfileObject?.identifier, dispatch, navigate, onlyOneProfile, profileData?.identifier],
  )

  useUserProfiles()

  useEffect(() => {
    showMenu(false)
    if (!isLoading) {
      SN.focus('#keep-or-create-btn')
    }
  }, [SN, isLoading, showMenu])

  if (isLoading) return <DotSpinner />
  return (
    <div className={classNames('delete-profile', loadingAction && 'is-loading')}>
      <div className="delete-profile-image-wrapper">
        <div style={{ backgroundColor: profileData?.image.backgroundColor }} className="delete-profile-image">
          <ProfileIcon profileData={profileData} />
        </div>
        <div className="delete-profile-name">{profileData?.name}</div>
      </div>
      <div className="delete-profile-info">
        <Text tag={TextTag.H1}>{onlyOneProfile ? V.errorLabelProfileDelete : V.deleteProfileAreYouSure}</Text>
        <Text tag={TextTag.P} className="delete-profile-subtitle">
          {onlyOneProfile ? V.deleteProfileSubtitleOne : V.deleteProfileSubtitleTwo}
        </Text>
      </div>
      <div className="delete-profile-buttons">
        <SecondaryButton id="keep-or-create-btn" onClick={keepOrCreateNewProfileAction}>
          {onlyOneProfile ? V.createNewProfileButtonLabel : V.keepProfileButtonLabel}
        </SecondaryButton>
        <SecondaryButton id="delete-or-cancel-btn" onClick={deleteProfileOrCancelAction}>
          {onlyOneProfile ? V.editProfileCancelButtonLabel : V.deleteProfile}
        </SecondaryButton>
      </div>
      {loadingAction && <DotSpinner />}
    </div>
  )
}
